import { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { supabase } from '../../supabase/client';
import { setAuthUser, setSession, setProfile } from '../../Redux/store';
import { loadFromCache, CACHE_KEYS } from '../../data/cache';
import AuthScreen from './AuthScreen';

const AuthContext = createContext(null);

/** Gate that checks auth state — works offline via Supabase session persistence + cache */
export function AuthGate({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    // Listen for auth state changes (login, logout, token refresh)
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const userId = session?.user?.id ?? null;
      setUser(session?.user ?? null);
      dispatch(setAuthUser(userId));
      dispatch(setSession(session));
      if (!userId) {
        dispatch(setProfile(null));
      }
    });

    // Try to get existing session — works offline because Supabase persists to localStorage
    supabase.auth.getSession()
      .then(({ data }) => {
        const session = data.session;
        if (session?.user) {
          setUser(session.user);
          dispatch(setAuthUser(session.user.id));
          dispatch(setSession(session));
        }
        setLoading(false);
      })
      .catch(() => {
        // Offline or Supabase unreachable — check cache for user id
        const cachedUserId = loadFromCache(CACHE_KEYS.AUTH_USER_ID);
        if (cachedUserId) {
          // Set a minimal user object so the gate doesn't show AuthScreen
          setUser({ id: cachedUserId });
          dispatch(setAuthUser(cachedUserId));
          const cachedProfile = loadFromCache(CACHE_KEYS.PROFILE);
          if (cachedProfile) dispatch(setProfile(cachedProfile));
        }
        setLoading(false);
      });

    return () => sub.subscription.unsubscribe();
  }, [dispatch]);

  if (loading) {
    return <div className="flex h-[100dvh] items-center justify-center text-muted-foreground">Loading…</div>;
  }

  if (!user) return <AuthScreen />;

  return (
    <AuthContext.Provider value={{ user, loading, supabase }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthGate');
  }
  return context;
}
